// PostHog for the StoreKit purchase path.
//
// THE ONE RULE IN THIS FILE: `payment_completed` is never emitted from here.
//
// server/posthog-capture.js is explicit about why. `payment_completed` used to
// fire client-side on /success, most payers never reached that page, and the
// dashboard read near zero while money was coming in. The fix was to move it to
// the one place that always runs when money moves: the payment provider's
// webhook. For Apple that place is server/revenuecat-webhook.js, which fires the
// same event name, through the same helper, with the same deterministic-uuid
// dedupe, so App Store revenue lands in the same funnel tiles as Stripe revenue
// (PostHog dashboard 521008) instead of splitting the report in two.
//
// Emitting it a second time from the device would be exactly the double-count
// trap that comment warns about. The events below are the funnel and diagnostic
// events around it, and none of them carries revenue.

import posthog from 'posthog-js';

const NATIVE_PROPS = { platform: 'ios', provider: 'apple', store: 'app_store' };

function capture(event, props) {
  try {
    posthog.capture(event, { ...NATIVE_PROPS, ...props });
  } catch (e) {
    /* analytics is best-effort, exactly as on the web paywall */
  }
}

/**
 * The StoreKit sheet is about to be presented. This is the native equivalent of
 * handing the Stripe link to a browser, so it uses the same event name and the
 * same `funnel` property, which is what keeps the existing
 * "initiated -> completed" funnel readable across both payment rails.
 *
 * The web paywall must not also fire payment_initiated on the same tap. The
 * native branch in Pricing.jsx returns before that code runs.
 */
export function trackNativeCheckoutStarted(props = {}) {
  capture('payment_initiated', {
    funnel: 'organic',
    handoff: 'storekit',
    in_app_browser: null,
    ...props,
  });
}

/**
 * StoreKit reported a completed purchase and RevenueCat confirmed the
 * entitlement. Revenue itself is reported by the webhook, so this event exists
 * only to measure the gap between the two (a large gap means webhook trouble).
 */
export function trackNativePurchaseSucceeded(props = {}) {
  capture('ios_purchase_completed', props);
}

/** The user dismissed the StoreKit sheet. Not an error and not a lost sale yet. */
export function trackNativePurchaseCancelled(props = {}) {
  capture('ios_purchase_cancelled', props);
}

/** StoreKit or RevenueCat refused the purchase. `code` is the RevenueCat error code. */
export function trackNativePurchaseFailed(props = {}) {
  capture('ios_purchase_failed', props);
}

/**
 * Result of "Restore purchase" on the native path. `restored` says whether an
 * App Store purchase came back; the web paywall still falls through to the
 * Stripe restore afterwards, which reports itself separately.
 */
export function trackNativeRestore(props = {}) {
  capture('ios_restore_attempted', props);
}
